import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  Card, 
  Row, 
  Col, 
  Badge, 
  Descriptions, 
  Table, 
  Collapse, 
  Button, 
  Modal, 
  Form, 
  Input, 
  Upload, 
  message,
  Divider,
  Typography,
  Space,
  Tag
} from "antd";
import { 
  ArrowLeftOutlined, 
  CheckOutlined, 
  CloseOutlined, 
  EditOutlined,
  DownloadOutlined,
  UploadOutlined,
  HistoryOutlined
} from "@ant-design/icons";
import { mockWorkOrders } from "../../data/mockWorkOrders";
import { WorkOrder, ActionFormData } from "../../types/workOrder";
import dayjs from "dayjs";

const { Panel } = Collapse;
const { TextArea } = Input;
const { Title, Text } = Typography;

interface WorkOrderDetailsProps {
  onStatusUpdate?: (workOrderId: string, newStatus: string, actionData: ActionFormData) => void;
}

const WorkOrderDetails: React.FC<WorkOrderDetailsProps> = ({ onStatusUpdate }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [workOrder, setWorkOrder] = useState<WorkOrder | null>(null);
  const [actionModalVisible, setActionModalVisible] = useState<boolean>(false);
  const [actionType, setActionType] = useState<'accept' | 'reject' | 'request_changes' | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    if (id) {
      const foundWorkOrder = mockWorkOrders.find(wo => wo.id === id);
      setWorkOrder(foundWorkOrder || null);
    }
  }, [id]);

  if (!workOrder) {
    return (
      <div className="nk-content">
        <div className="nk-content-inner">
          <div className="nk-content-body">
            <div className="text-center py-5">
              <h5>Work Order Not Found</h5>
              <p className="text-soft">The requested work order could not be found.</p>
              <Button onClick={() => navigate('/New-work-order')}>
                Back to Work Orders
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleActionSubmit = async (values: any) => {
    if (!actionType) return;

    const actionData: ActionFormData = {
      actionType,
      ...values
    };

    try {
      // In a real app, this would make an API call
      console.log('Submitting action:', actionData);
      
      if (onStatusUpdate) {
        let newStatus = workOrder.status;
        if (actionType === 'accept') newStatus = 'Accepted';
        else if (actionType === 'reject') newStatus = 'Rejected';
        else if (actionType === 'request_changes') newStatus = 'Under Negotiation';
        
        onStatusUpdate(workOrder.id, newStatus, actionData);
      }

      message.success(`Work order ${actionType === 'accept' ? 'accepted' : actionType === 'reject' ? 'rejected' : 'change requested'} successfully!`);
      setActionModalVisible(false);
      form.resetFields();
      
      // Navigate based on action
      if (actionType === 'accept') {
        navigate('/work-order-execution');
      } else if (actionType === 'request_changes') {
        navigate('/work-order-contractor-approvals');
      } else {
        navigate('/New-work-order');
      }
    } catch (error) {
      message.error('Failed to submit action. Please try again.');
    }
  };

  const openActionModal = (type: 'accept' | 'reject' | 'request_changes') => {
    setActionType(type);
    setActionModalVisible(true);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      'Pending': { color: 'processing', text: 'Pending Review' },
      'Accepted': { color: 'success', text: 'Accepted' },
      'Rejected': { color: 'error', text: 'Rejected' },
      'Under Negotiation': { color: 'warning', text: 'Under Negotiation' },
      'Finalized': { color: 'success', text: 'Finalized' }
    };
    const config = statusConfig[status as keyof typeof statusConfig] || { color: 'default', text: status };
    return <Badge status={config.color as any} text={config.text} />;
  };

  const workOrderLinesColumns = [
    {
      title: 'Line ID',
      dataIndex: 'lineId',
      key: 'lineId',
    },
    {
      title: 'Task Name',
      dataIndex: 'taskName',
      key: 'taskName',
    },
    {
      title: 'Work Category',
      dataIndex: 'workCategory',
      key: 'workCategory',
    },
    {
      title: 'Qty',
      dataIndex: 'quantity',
      key: 'quantity',
      align: 'right' as const,
    },
    {
      title: 'UOM',
      dataIndex: 'uom',
      key: 'uom',
    },
    {
      title: 'Rate',
      dataIndex: 'rate',
      key: 'rate',
      align: 'right' as const,
      render: (value: number) => formatCurrency(value),
    },
    {
      title: 'Tax (%)',
      dataIndex: 'tax',
      key: 'tax',
      align: 'right' as const,
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      align: 'right' as const,
      render: (value: number) => formatCurrency(value),
    },
  ];

  const canTakeAction = workOrder.status === 'Pending';

  return (
    <div className="nk-content">
      <div className="nk-content-inner">
        <div className="nk-content-body">
          {/* Header */}
          <div className="nk-block-head">
            <div className="nk-block-between">
              <div className="nk-block-head-content">
                <Button
                  icon={<ArrowLeftOutlined />}
                  onClick={() => navigate('/New-work-order')}
                  type="link"
                  className="mb-2"
                >
                  Back to Work Orders
                </Button>
                <Title level={3} style={{ margin: 0 }}>
                  {workOrder.woNumber}
                </Title>
                <Space className="mt-2">
                  {getStatusBadge(workOrder.status)}
                  {workOrder.slaAge > 2 && <Tag color="error">SLA Breach ({workOrder.slaAge} days)</Tag>}
                </Space>
              </div>
              <div className="nk-block-head-content">
                {canTakeAction && (
                  <Space>
                    <Button
                      type="primary"
                      icon={<CheckOutlined />}
                      onClick={() => openActionModal('accept')}
                      style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                    >
                      Accept
                    </Button>
                    <Button
                      icon={<EditOutlined />}
                      onClick={() => openActionModal('request_changes')}
                    >
                      Request Changes
                    </Button>
                    <Button
                      danger
                      icon={<CloseOutlined />}
                      onClick={() => openActionModal('reject')}
                    >
                      Reject
                    </Button>
                  </Space>
                )}
                {workOrder.changeHistory && workOrder.changeHistory.length > 0 && (
                  <Button icon={<HistoryOutlined />} className="ml-2">
                    View History
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* General Information */}
          <div className="nk-block">
            <Card title="General Information" className="mb-4">
              <Row gutter={[24, 16]}>
                <Col span={24} lg={12}>
                  <Descriptions column={1} size="small">
                    <Descriptions.Item label="WO Title">
                      <Text strong>{workOrder.title}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Description">
                      {workOrder.description}
                    </Descriptions.Item>
                    <Descriptions.Item label="Site / Project">
                      {workOrder.site} / {workOrder.project}
                    </Descriptions.Item>
                    <Descriptions.Item label="Contract Type">
                      <Tag>{workOrder.contractType}</Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Contractor">
                      {workOrder.contractorName}
                    </Descriptions.Item>
                  </Descriptions>
                </Col>
                <Col span={24} lg={12}>
                  <Descriptions column={1} size="small">
                    <Descriptions.Item label="WO Value">
                      <Text strong style={{ color: '#1890ff', fontSize: '16px' }}>
                        {formatCurrency(workOrder.woValue)}
                      </Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Date Released">
                      {dayjs(workOrder.dateReleased).format('DD/MM/YYYY')}
                    </Descriptions.Item>
                    <Descriptions.Item label="Planned Duration">
                      {dayjs(workOrder.plannedStartDate).format('DD/MM/YYYY')} - {dayjs(workOrder.plannedEndDate).format('DD/MM/YYYY')}
                    </Descriptions.Item>
                    <Descriptions.Item label="Contract Owner">
                      {workOrder.contractOwner}
                    </Descriptions.Item>
                    <Descriptions.Item label="Linked Work Request">
                      {workOrder.linkedWorkRequest && <Tag color="blue">{workOrder.linkedWorkRequest}</Tag>}
                    </Descriptions.Item>
                  </Descriptions>
                </Col>
              </Row>
            </Card>

            {/* Work Order Lines */}
            <Card title="Work Order Lines" className="mb-4">
              <Table
                columns={workOrderLinesColumns}
                dataSource={workOrder.workOrderLines}
                rowKey="lineId"
                pagination={false}
                scroll={{ x: 800 }}
                summary={(pageData) => {
                  const total = pageData.reduce((sum, record) => sum + record.total, 0);
                  return (
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0} colSpan={7}>
                        <Text strong>Total</Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={1} align="right">
                        <Text strong>{formatCurrency(total)}</Text>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  );
                }}
              />
            </Card>

            {/* Collapsible Sections */}
            <Collapse className="mb-4">
              <Panel header="General Conditions" key="1">
                <ul>
                  {workOrder.generalConditions.map((condition, index) => (
                    <li key={index} className="mb-2">{condition}</li>
                  ))}
                </ul>
              </Panel>
              
              <Panel header="Payment Conditions" key="2">
                <Row gutter={[24, 16]}>
                  <Col span={24} md={12}>
                    <Card size="small" title="Mobilization Advance">
                      <p><strong>Percentage:</strong> {workOrder.paymentConditions.mobilizationAdvance.percentage}%</p>
                      <p><strong>Amount:</strong> {formatCurrency(workOrder.paymentConditions.mobilizationAdvance.amount)}</p>
                    </Card>
                  </Col>
                  <Col span={24} md={12}>
                    <Card size="small" title="Retention Amount">
                      <p><strong>Percentage:</strong> {workOrder.paymentConditions.retentionAmount.percentage}%</p>
                      <p><strong>Release Date:</strong> {dayjs(workOrder.paymentConditions.retentionAmount.releaseDate).format('DD/MM/YYYY')}</p>
                    </Card>
                  </Col>
                  <Col span={24} md={12}>
                    <Card size="small" title="On Hold Amount">
                      <p><strong>Amount:</strong> {formatCurrency(workOrder.paymentConditions.onHoldAmount.amount)}</p>
                      <p><strong>Release Date:</strong> {dayjs(workOrder.paymentConditions.onHoldAmount.releaseDate).format('DD/MM/YYYY')}</p>
                    </Card>
                  </Col>
                  <Col span={24} md={12}>
                    <Card size="small" title="Payment Terms">
                      <p><strong>Pay on Completion:</strong> {workOrder.paymentConditions.payOnCompletion ? 'Yes' : 'No'}</p>
                    </Card>
                  </Col>
                </Row>
              </Panel>
              
              <Panel header="Attachments" key="3">
                <Row gutter={[16, 16]}>
                  {workOrder.attachments.map((attachment) => (
                    <Col key={attachment.id} span={24} md={12}>
                      <Card size="small">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <Text strong>{attachment.fileName}</Text>
                            <br />
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                              {attachment.fileType} • {dayjs(attachment.uploadDate).format('DD/MM/YYYY')}
                            </Text>
                          </div>
                          <Button icon={<DownloadOutlined />} size="small">
                            Download
                          </Button>
                        </div>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </Panel>
            </Collapse>

            {/* Action History */}
            {workOrder.contractorActions.length > 0 && (
              <Card title="Action History" className="mb-4">
                {workOrder.contractorActions.map((action, index) => (
                  <div key={index} className="mb-3 p-3" style={{ backgroundColor: '#f5f5f5', borderRadius: '6px' }}>
                    <Row gutter={[16, 8]}>
                      <Col span={24} md={8}>
                        <Text strong>Action:</Text> <Tag color="blue">{action.actionType.replace('_', ' ').toUpperCase()}</Tag>
                      </Col>
                      <Col span={24} md={8}>
                        <Text strong>Date:</Text> {dayjs(action.timestamp).format('DD/MM/YYYY HH:mm')}
                      </Col>
                      <Col span={24} md={8}>
                        <Text strong>By:</Text> {action.authorizedPersonName}
                      </Col>
                      {action.reason && (
                        <Col span={24}>
                          <Text strong>Reason:</Text> {action.reason}
                        </Col>
                      )}
                      {action.comments && (
                        <Col span={24}>
                          <Text strong>Comments:</Text> {action.comments}
                        </Col>
                      )}
                    </Row>
                  </div>
                ))}
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Action Modal */}
      <Modal
        title={`${actionType === 'accept' ? 'Accept' : actionType === 'reject' ? 'Reject' : 'Request Changes'} Work Order`}
        open={actionModalVisible}
        onCancel={() => {
          setActionModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleActionSubmit}
        >
          {actionType === 'accept' && (
            <>
              <Form.Item
                name="authorizedPersonName"
                label="Authorized Signatory Name"
                rules={[{ required: true, message: 'Please enter authorized person name' }]}
              >
                <Input placeholder="Enter full name" />
              </Form.Item>
              <Form.Item
                name="digitalSignature"
                label="Digital Signature"
                rules={[{ required: true, message: 'Please provide digital signature' }]}
              >
                <Input placeholder="Digital signature or OTP authentication" />
              </Form.Item>
            </>
          )}

          {actionType === 'reject' && (
            <Form.Item
              name="reason"
              label="Reason for Rejection"
              rules={[{ required: true, message: 'Please provide reason for rejection' }]}
            >
              <TextArea rows={4} placeholder="Please explain why you are rejecting this work order..." />
            </Form.Item>
          )}

          {actionType === 'request_changes' && (
            <>
              <Form.Item
                name="comments"
                label="Change Request Details"
                rules={[{ required: true, message: 'Please describe the requested changes' }]}
              >
                <TextArea rows={4} placeholder="Describe what changes you would like to request..." />
              </Form.Item>
              <Form.Item
                name="attachments"
                label="Supporting Documents (Optional)"
              >
                <Upload>
                  <Button icon={<UploadOutlined />}>Upload Files</Button>
                </Upload>
              </Form.Item>
            </>
          )}

          <div className="text-right">
            <Space>
              <Button onClick={() => setActionModalVisible(false)}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                Submit {actionType === 'accept' ? 'Acceptance' : actionType === 'reject' ? 'Rejection' : 'Change Request'}
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default WorkOrderDetails;